import { makeObservable, observable, runInAction } from "mobx";

import { CourseCollection } from "@courses-models/course";
import { AuthenticatedUserModel } from "@courses-utils/gateway-auth";
import { autobind, throttleToAnimationFrame } from "@use/decorators";

/**
 * Root data store for the application lifecycle.
 *
 * This store will combine all application state, making it straight forward to
 * propagate this throughout the application.
 */
export class Store {
  /**
   * Specifies whether or not the search bar is currently open.
   */
  public searchbarOpen: boolean;

  /**
   * Specifies if a dropdown is currently displayed, so that the rest of the
   * interface can adjust it's behaviour accordingly.
   */
  public isDropdownOpen: boolean;

  /**
   * Specifies if a lightbox is currently displayed, so that the rest of the
   * interface can adjust it's behaviour accordingly.
   */
  public isLightboxOpen: boolean;

  /**
   * Specifies whether or not the viewport is currently in a state that should
   * be recognized as mobile.
   */
  public isMobile: boolean = this.determineCurrentBreakpoint();

  /**
   * Apply initial data when the store is created, so it's instantly available
   * when referencing items on the store.
   */
  constructor() {
    window.addEventListener("resize", this.onResize);
    window.addEventListener("orientationchange", this.onResize);

    makeObservable(this, {
      searchbarOpen: observable,
      isDropdownOpen: observable,
      isLightboxOpen: observable,
      isMobile: observable,
    });
  }

  /**
   * Simple helper that determines whether or not we're currently in a mobile
   * breakpoint
   */
  private determineCurrentBreakpoint(): boolean {
    return window.innerWidth < 768;
  }

  /**
   * Listener that'll get triggered every time the viewport is updated
   */
  @autobind()
  @throttleToAnimationFrame()
  private onResize(): void {
    runInAction(() => {
      this.isMobile = this.determineCurrentBreakpoint();
    });
  }
}
